import { useState } from 'react';
import { Row, Space, Typography } from 'antd';
import { MinusCircleOutlined } from '@ant-design/icons';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import { StyledTextArea } from 'components/new-ui/base/text-area/styles';
import { ErrorLabel } from 'components/new-ui/composed/formTextArea/styles';
import Notification from 'components/ui/base/notification/Notification';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import { InvoicesStatuses } from 'store/invoices/types';
import useInvoices from 'hooks/useInvoices';
import { Events } from 'utils/events.enum';

const MAX_REASON_LENGTH = 999;

type Props = {
    invoiceId: string;
    open: boolean;
    onClose: () => void;
};

const InvoiceCancelModal = ({ invoiceId, open, onClose }: Props) => {
    const { updateInvoiceStatus } = useInvoices();
    const [reason, setReason] = useState('');
    const [touched, setTouched] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const trimmed = reason.trim();
    const isRequiredError = touched && trimmed === '';
    const isTooLongError = touched && trimmed.length > MAX_REASON_LENGTH;
    const hasError = isRequiredError || isTooLongError;

    const handleClose = () => {
        onClose();
        setReason('');
        setTouched(false);
    };

    const handleConfirm = async () => {
        setTouched(true);

        if (trimmed === '' || trimmed.length > MAX_REASON_LENGTH) {
            return;
        }

        try {
            setIsSubmitting(true);

            await updateInvoiceStatus(invoiceId, {
                status: InvoicesStatuses.CANCELLED,
                cancellationReason: trimmed,
            });

            handleClose();

            Notification({
                type: 'success',
                messageTitle: 'Invoice Cancelled',
                description: 'Invoce successfully cancelled.',
            });

            window.EventBus.publish(Events.INVOICE_HISTORY_UPDATED);
        } catch (error) {
            const errMessage = getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE;
            Notification({
                type: 'error',
                messageTitle: 'Operation Failed',
                description: errMessage,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ConfirmationModal
            open={open}
            onCancel={handleClose}
            title="Mark as Cancelled"
            confirmButtonText="Cancel Invoice"
            confirmationButtonDisabled={hasError}
            confirmationButtonLoading={isSubmitting}
            onConfirm={handleConfirm}
            type="error"
            icon={<MinusCircleOutlined />}
            content={
                <>
                    <Typography.Text style={{ textAlign: 'left', width: '100%' }}>
                        Please provide a reason.
                    </Typography.Text>

                    <Row justify="space-between" align="top" style={{ width: '100%' }}>
                        <Space size={4}>
                            <LabelRequiredSymbol />
                            Reason :
                        </Space>
                        <div style={{ width: 360 }}>
                            <StyledTextArea
                                rows={3}
                                $hasError={hasError}
                                placeholder="Enter reason for cancellation"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                style={{ width: '100%' }}
                                onBlur={() => setTouched(true)}
                            />
                            {isRequiredError && <ErrorLabel>Reason is required.</ErrorLabel>}
                            {isTooLongError && (
                                <ErrorLabel>Reason cannot exceed {MAX_REASON_LENGTH} characters.</ErrorLabel>
                            )}
                        </div>
                    </Row>
                </>
            }
        />
    );
};

export default InvoiceCancelModal;
